import React, { useState, useContext, useEffect } from 'react';
import { AccountContext } from '../../../components/AccountContext';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import gql from 'graphql-tag';
import moment from 'moment-timezone';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { Loading } from '../../../components/Loading';
import { growlErrors } from '../../../utils/NotificationUtil';
import { useQuery } from '@apollo/react-hooks';
import SettledCommissionNotice from './SettledCommissionNotice';
import { AccountType, AppConstants } from '../../../shared/Constants';
import Invoice from '../../../components/Invoice';

const SETTLED_COMMISSIONS_QUERY = gql`
  query settledCommissionsQuery($accountId: ID!) {
    account(id: $accountId) {
      settledCommissions {
        accountId
        accountName
        accountType
        invoiceId
        invoiceNumber
        commissionValue
        settledOn
        settledThrough
        accountInvoiceUrl
        commissionSpecificationUrl
      }
    }
  }
`;

const i18nMessages = defineMessages({
  settledCommission: {
    id: 'menu.settled-commission',
    defaultMessage: 'Settled commission',
  },
  client: { id: 'attrs.client', defaultMessage: 'Client' },
  id: { id: 'attrs.id', defaultMessage: 'ID' },
  type: { id: 'attrs.type', defaultMessage: 'Type' },
  clientType: { id: 'account-type.client-long', defaultMessage: 'Client' },
  orderNumber: { id: 'account.settled-commissions.order-number', defaultMessage: 'Order#' },
  commission: { id: 'account.settled-commissions.commission', defaultMessage: 'Commission' },
  settledOn: { id: 'account.settled-commissions.settled-on', defaultMessage: 'Settled on' },
  yourInvoice: { id: 'account.settled-commissions.your-invoice', defaultMessage: 'Your invoice' },
  specification: {
    id: 'account.settled-commissions.specification',
    defaultMessage: 'Specification',
  },
  noSettledCommissions: {
    id: 'account.settled-commissions.no-settled-commissions',
    defaultMessage: 'There are currently no settled commissions',
  },
  download: {
    id: 'attrs.download',
    defaultMessage: 'Download',
  },
});

const SettledCommission = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const [settledCommissions, setSettledCommissions] = useState([]);

  const { loading, error, data } = useQuery(SETTLED_COMMISSIONS_QUERY, {
    variables: {
      accountId: currentAccount,
    },
    fetchPolicy: 'no-cache',
  });

  const { account } = data;

  useEffect(() => {
    if (account) {
      setSettledCommissions(account.settledCommissions);
    }
    // eslint-disable-next-line
  }, [account]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const getAccountTypeLabel = (accountType) => {
    switch (accountType) {
      case AccountType.NBM_CLIENT:
        return formatMessage(i18nMessages.clientType);
      case AccountType.GP:
        return 'GP';
      case AccountType.ESSENTIAL:
        return 'Essential';
      default:
        return '';
    }
  };

  return (
    <>
      <SettledCommissionNotice />
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel header={formatMessage(i18nMessages.settledCommission)}>
              {settledCommissions.length === 0 && (
                <div className="align-center font-weight-600">{formatMessage(i18nMessages.noSettledCommissions)}</div>
              )}
              {settledCommissions.length > 0 && (
                <>
                  <div className="font-weight-600 flex-space-between">
                    <div>{formatMessage(i18nMessages.client)}</div>
                    <div>{formatMessage(i18nMessages.id)}</div>
                    <div>{formatMessage(i18nMessages.type)}</div>
                    <div>{formatMessage(i18nMessages.orderNumber)}</div>
                    <div>{formatMessage(i18nMessages.commission)}</div>
                    <div>{formatMessage(i18nMessages.settledOn)}</div>
                    <div>{formatMessage(i18nMessages.yourInvoice)}</div>
                    <div>{formatMessage(i18nMessages.specification)}</div>
                  </div>
                  <>
                    {settledCommissions.map((settledCommission) => {
                      return (
                        <div key={settledCommission.id} className="flex-space-between">
                          <div>{settledCommission.accountName}</div>
                          <div>{settledCommission.accountId}</div>
                          <div>{getAccountTypeLabel(settledCommission.accountType)}</div>
                          <Invoice
                            managingAccountId={currentAccount}
                            clientAccountId={settledCommission.accountId}
                            invoiceId={settledCommission.invoiceId}
                            invoiceNumber={settledCommission.invoiceNumber}
                          />
                          <div>${settledCommission.commissionValue.toFixed(2)}</div>
                          <div>{moment(settledCommission.settledOn).format(AppConstants.DATE_INPUT_FORMAT)}</div>
                          <div>
                            <a href={settledCommission.accountInvoiceUrl} target="_blank" rel="noreferrer noopener">
                              {settledCommission.settledThrough}
                            </a>
                          </div>
                          <div>
                            <a
                              href={settledCommission.commissionSpecificationUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              {formatMessage(i18nMessages.download)}
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </>
                </>
              )}
            </Panel>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettledCommission;
